/**
 * localStorage adapter behind `chatDraftTextAtomFamily`.
 *
 * The draft is written on every debounced keystroke, so under `atomWithStorage`'s
 * default adapter a full origin turned each one into an uncaught
 * `QuotaExceededError`. The origin is shared, so the draft is rarely what filled
 * it; losing a draft's persistence is the right degradation, throwing from the
 * composer is not.
 *
 * A cleared draft is removed rather than stored as `""`: every chat that ever
 * had a draft otherwise keeps a key on disk for good, which is how these keys
 * became a long tail on the users who hit the quota. Abandoned drafts still
 * pile up, so each one is stamped on disk and the store is held to the newest
 * `MAX_PERSISTED_DRAFTS`, none older than `DRAFT_TTL_MS`; otherwise they starve
 * every other store on the origin (Flagsmith's flag cache among them).
 */
import { getSafeLocalStorage } from '@/lib/jotai-storage';
import { logger } from '@/utils/logger/logger';
import { createJSONStorage } from 'jotai/utils';

export const CHAT_DRAFT_KEY_PREFIX = 'heidi-chat-draft-';

/**
 * A pasted note can be far larger than anything worth restoring after a
 * reload. Past this the draft lives in memory only, and the stale shorter copy
 * on disk is dropped so a reload does not restore a truncated-looking draft.
 */
const MAX_PERSISTED_DRAFT_CHARS = 8_192;

const MAX_PERSISTED_DRAFTS = 25;

const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const EMPTY_DRAFT = JSON.stringify('');

const TOP_KEY_SHAPES = 5;

const QUOTA_ERROR_NAMES = new Set(['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED']);

const warnedFailures = new Set<string>();

const warnOnce = (dedupeToken: string, message: string, context?: object): void => {
  if (warnedFailures.has(dedupeToken)) return;
  warnedFailures.add(dedupeToken);
  logger.warn(message, context);
};

const errorNameOf = (error: unknown): string => {
  const name = (error as { name?: unknown } | null)?.name;
  return typeof name === 'string' ? name : typeof error;
};

/**
 * Collapses ids, emails and long tokens so the key names say which store it is
 * (`heidi-chat-draft-*`, `FLAGSMITH_DB_*`) without saying whose.
 */
const keyShape = (key: string): string =>
  key
    .replace(/[^\s:/|]*@[^\s:/|]*/g, '*')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '*')
    .replace(/[A-Za-z0-9]{16,}/g, '*')
    .replace(/\d{3,}/g, '*');

/**
 * What filled the origin is not the draft store's to guess, so the first
 * refused write reports the largest stores by size. Names are shapes and sizes
 * are character counts; no values leave the device.
 */
const describeStorageUsage = () => {
  const bytesByShape = new Map<string, number>();
  let totalChars = 0;
  for (const key of Object.keys(window.localStorage)) {
    const size = key.length + (window.localStorage.getItem(key)?.length ?? 0);
    totalChars += size;
    const shape = keyShape(key);
    bytesByShape.set(shape, (bytesByShape.get(shape) ?? 0) + size);
  }
  const largest = [...bytesByShape.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, TOP_KEY_SHAPES)
    .map(([shape, chars]) => ({ shape, chars }));
  return { totalChars, keyCount: window.localStorage.length, largest };
};

const reportStorageFailure = (operation: string, error: unknown): void => {
  if (warnedFailures.has(operation)) return;
  const errorName = errorNameOf(error);
  let usage: ReturnType<typeof describeStorageUsage> | undefined;
  if (operation === 'set' && QUOTA_ERROR_NAMES.has(errorName)) {
    try {
      usage = describeStorageUsage();
    } catch {
      // Reading storage failed too; the failure itself is still worth reporting.
    }
  }
  warnOnce(operation, `[chatDraft] localStorage ${operation} failed`, {
    errorName,
    usage,
  });
};

const draftKeysOnDisk = (): string[] =>
  Object.keys(window.localStorage).filter((key) => key.startsWith(CHAT_DRAFT_KEY_PREFIX));

type StoredDraft = { savedAt?: number; value: string | null };

/** Older builds stored the bare JSON value; it still reads, unstamped. */
const readStoredDraft = (raw: string | null): StoredDraft => {
  if (raw === null) return { value: null };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object' && 'draft' in parsed) {
      const { draft, savedAt } = parsed as { draft: unknown; savedAt?: unknown };
      return {
        savedAt: typeof savedAt === 'number' ? savedAt : undefined,
        value: JSON.stringify(draft),
      };
    }
  } catch {
    // Not JSON: hand it to jotai as-is, which falls back to the initial value.
  }
  return { value: raw };
};

// `value` is already JSON, so it is embedded rather than re-escaped.
const stampDraft = (value: string): string =>
  `{"savedAt":${Date.now()},"draft":${value}}`;

/**
 * Drops empty and expired drafts, then the oldest past the cap. Unstamped
 * drafts from older builds rank oldest but are not expired, since their age is
 * unknown. `incomingKey` is about to be written, so it is left alone and
 * counted against the cap.
 */
const pruneDrafts = (
  storage: ReturnType<typeof getSafeLocalStorage>,
  incomingKey?: string
): void => {
  try {
    const now = Date.now();
    const kept: Array<{ key: string; savedAt: number }> = [];
    for (const key of draftKeysOnDisk()) {
      if (key === incomingKey) continue;
      const raw = storage.getItem(key);
      const { savedAt } = readStoredDraft(raw);
      if (
        raw === EMPTY_DRAFT ||
        (savedAt !== undefined && now - savedAt > DRAFT_TTL_MS)
      ) {
        storage.removeItem(key);
      } else {
        kept.push({ key, savedAt: savedAt ?? 0 });
      }
    }
    const room =
      incomingKey === undefined ? MAX_PERSISTED_DRAFTS : MAX_PERSISTED_DRAFTS - 1;
    kept
      .sort((a, b) => b.savedAt - a.savedAt)
      .slice(room)
      .forEach(({ key }) => storage.removeItem(key));
  } catch (error) {
    reportStorageFailure('sweep', error);
  }
};

const sweepListeners = new Set<() => void>();

const buildStringStorage = () => {
  const safe = getSafeLocalStorage((operation, _key, error) =>
    reportStorageFailure(operation, error)
  );

  if (typeof window === 'undefined') return safe;

  pruneDrafts(safe);

  return {
    ...safe,
    getItem: (key: string) => readStoredDraft(safe.getItem(key)).value,
    setItem: (key: string, value: string) => {
      if (value === EMPTY_DRAFT) {
        safe.removeItem(key);
        return;
      }
      if (value.length > MAX_PERSISTED_DRAFT_CHARS) {
        safe.removeItem(key);
        warnOnce(
          'oversized',
          '[chatDraft] draft too large to persist; kept in memory only',
          {
            chars: value.length,
          }
        );
        return;
      }
      if (safe.getItem(key) === null) pruneDrafts(safe, key);
      safe.setItem(key, stampDraft(value));
    },
    // jotai wires its own cross-tab `storage` listener only when the adapter IS
    // `window.localStorage`, which a safe wrapper never is. The in-tab set is
    // how the logout sweep reaches mounted atoms without importing Chat.
    subscribe: (key: string, callback: (value: string | null) => void) => {
      const onStorage = (event: StorageEvent) => {
        if (event.storageArea === window.localStorage && event.key === key) {
          callback(readStoredDraft(event.newValue).value);
        }
      };
      const onSweep = () => callback(null);
      window.addEventListener('storage', onStorage);
      sweepListeners.add(onSweep);
      return () => {
        window.removeEventListener('storage', onStorage);
        sweepListeners.delete(onSweep);
      };
    },
  };
};

let stringStorage: ReturnType<typeof buildStringStorage> | undefined;

const getStringStorage = () => (stringStorage ??= buildStringStorage());

export const chatDraftStorage = createJSONStorage<string>(getStringStorage);

/**
 * Drafts hold clinician-typed text in unscoped storage, so a full logout drops
 * every one of them. Mounted draft atoms are emptied too, so a sign-out that
 * never reloads cannot show the next clinician the previous one's text; an
 * unmounted atom re-reads storage when it next mounts.
 */
export const removeAllChatDraftsFromStorage = (): void => {
  if (typeof window === 'undefined') return;
  try {
    for (const key of draftKeysOnDisk()) window.localStorage.removeItem(key);
  } catch (error) {
    reportStorageFailure('logout-sweep', error);
  }
  sweepListeners.forEach((listener) => listener());
};
